// components/Header.tsx
"use client";

import { useEffect, useState } from "react";
import MegaMenu from "./menu";
import { useSelector, useDispatch } from "react-redux";
import type { RootState, AppDispatch } from "../../store/store";
import {
  fetchHeader,
  setActiveMenuLabel,
  setActiveMenuLink,
} from "@/store/headerSlice";
import { usePathname } from "next/navigation";

const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const { data, error, activeMenuLabel, activeMenuLink, isFilterOpen } =
    useSelector((state: RootState) => state.header);

  const pathname = usePathname();

  // Fetch header + restore from localStorage
  useEffect(() => {
    if (!data?.data?.id) dispatch(fetchHeader());

    const savedLabel = localStorage.getItem("activeMenuLabel");
    const savedLink = localStorage.getItem("activeMenuLink");

    if (savedLabel) dispatch(setActiveMenuLabel(savedLabel));
    if (savedLink) dispatch(setActiveMenuLink(savedLink));
  }, [dispatch, data]);

  // Sync with URL
  useEffect(() => {
    if (pathname) dispatch(setActiveMenuLink(pathname));
  }, [pathname, dispatch]);

  // Reset hovered and mobile menu on page change
  useEffect(() => {
    setHovered(false);
    setIsOpen(false);
  }, [pathname]);

  // Save changes back to localStorage
  useEffect(() => {
    if (activeMenuLabel)
      localStorage.setItem("activeMenuLabel", activeMenuLabel);
    if (activeMenuLink) localStorage.setItem("activeMenuLink", activeMenuLink);
  }, [activeMenuLabel, activeMenuLink]);

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSetActiveMenuLabel = (label: string | null) =>
    dispatch(setActiveMenuLabel(label));
  const handleSetActiveMenuLink = (link: string | null) =>
    dispatch(setActiveMenuLink(link));

  if (error) {
    console.error("Error loading header data error:", error);
    return <></>;
  }

  const setMenuToggle = (flag: boolean) => setIsOpen(flag);

  // Determine solid background state
  const isSolid = hovered || scrolled || isOpen || isFilterOpen;

  return (
    <header
      className="fixed w-full left-0 right-0 z-50 transition-[border-color,top] duration-300 ease-in-out"
      style={{
        top: "var(--df-banner-offset, 0px)",
        // Set by MegaMenu while it locks scroll, so the header doesn't shift
        // when the scrollbar disappears.
        paddingRight: "var(--menu-scrollbar-gap, 0px)",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Solid Background Reveal Layer */}
      <div
        className={`absolute inset-0 bg-white border-b border-[#ebebeb] -z-10 transition-transform duration-300 ease-in-out ${
          isSolid ? "translate-y-0" : "-translate-y-full"
        }`}
      />

      <MegaMenu
        data={data}
        Hover={hovered}
        Scrolled={scrolled || isFilterOpen}
        setHovered={setHovered} // pass setter to mega menu
        setActiveMenu={handleSetActiveMenuLabel}
        setActiveMenuLink={handleSetActiveMenuLink}
        activeMenuLabel={activeMenuLabel}
        activeMenuLink={activeMenuLink}
        setMenuToggle={setMenuToggle}
      />
    </header>
  );
};

export default Header;
